// SPDX-License-Identifier: AGPL-3.0-or-later
// SPDX-FileCopyrightText: 2026 Dobrev IT Ltd

import { isNativeApp } from "./server";

// In the native app, the sensitive localStorage entries — the session token and the
// device-key keyring — are mirrored to Keystore-backed secure storage (Android
// Keystore / iOS Keychain) for encryption at rest, matching the RN client. On the
// web this is a no-op (the SPA is served by its homeserver; localStorage is fine).

function isSensitive(k: string): boolean {
  // session token + device-key keyring + the stable device id (so re-login keeps the same device,
  // and its key, across a WebView data clear).
  return k === "zalmoxis.session" || k.startsWith("zalmoxis.key.") || k.startsWith("zalmoxis.device.");
}

// secure-storage keys must be simple; derive a stable, reversible-via-stored-payload key.
function skey(k: string): string {
  return "ls_" + btoa(unescape(encodeURIComponent(k))).replace(/[^a-zA-Z0-9]/g, "");
}

// initNativeStorage restores the secure entries into localStorage BEFORE the app
// reads them, then patches setItem/removeItem to mirror future changes. Call (and
// await) it once on startup, before rendering.
export async function initNativeStorage(): Promise<void> {
  if (!isNativeApp()) {
    return;
  }
  // Load the native plugin only inside the app (never in the browser bundle path).
  const { SecureStoragePlugin } = await import("capacitor-secure-storage-plugin");
  // 1) Restore: Keystore → localStorage.
  try {
    const { value: keys } = await SecureStoragePlugin.keys();
    for (const sk of keys) {
      if (!sk.startsWith("ls_")) {
        continue;
      }
      try {
        const { value } = await SecureStoragePlugin.get({ key: sk });
        const { k, v } = JSON.parse(value) as { k: string; v: string };
        window.localStorage.setItem(k, v);
      } catch {
        /* skip a corrupt/missing entry */
      }
    }
  } catch {
    /* nothing stored yet */
  }

  // 2) Mirror: patch localStorage so sensitive writes/removes reach the Keystore.
  const ls = window.localStorage;
  const origSet = ls.setItem.bind(ls);
  const origRemove = ls.removeItem.bind(ls);
  ls.setItem = (k: string, v: string) => {
    origSet(k, v);
    if (isSensitive(k)) {
      void SecureStoragePlugin.set({ key: skey(k), value: JSON.stringify({ k, v }) }).catch(() => undefined);
    }
  };
  ls.removeItem = (k: string) => {
    origRemove(k);
    if (isSensitive(k)) {
      void SecureStoragePlugin.remove({ key: skey(k) }).catch(() => undefined);
    }
  };
}
